// GAVION suspect emails: TS twin of supabase/schemas/89_gavion_suspect_emails.sql (private.dq_osa_distance,
// dq_mailbox_provider, dq_domain_label, dq_valid_email, dq_typo_distance, dq_suspect_emails), used by the FakeRest demo
// client (dataQuality/api/localDataQualityClient.ts). Same rule, same row shape as public.data_quality_suspect_emails.
import { isGenericHost, websiteHost } from "./dataQuality";

export type SuspectReason = "typo_domain" | "bounced";

export type SuspectEmail = {
  company_id: number;
  name: string | null;
  email: string;
  reasons: SuspectReason[];
  suggestion: string | null;
  evidence: {
    typo_domain?: string;
    typo_source?: "contact" | "website";
    bounced_at?: string;
    /** A bounce suppression: sends stay blocked until the email changes. */
    suppressed?: true;
  };
};

/** Optimal string alignment distance (adjacent transposition = 1). */
export const osaDistance = (a: string, b: string): number => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  let prev2: number[] = [];
  let prev = Array.from({ length: b.length + 1 }, (_, k) => k);
  for (let i = 1; i <= a.length; i++) {
    const cur = [i];
    for (let j = 1; j <= b.length; j++) {
      let v = Math.min(
        prev[j] + 1,
        cur[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      );
      if (i > 1 && j > 1 && a[i - 1] === b[j - 2] && a[i - 2] === b[j - 1])
        v = Math.min(v, prev2[j - 2] + 1);
      cur[j] = v;
    }
    prev2 = prev;
    prev = cur;
  }
  return prev[b.length];
};

export const MAILBOX_PROVIDERS: ReadonlySet<string> = new Set([
  "gmail.com",
  "hotmail.com",
  "hotmail.ca",
  "outlook.com",
  "outlook.fr",
  "live.com",
  "live.ca",
  "yahoo.com",
  "yahoo.ca",
  "icloud.com",
  "me.com",
  "msn.com",
  "videotron.ca",
  "bell.net",
  "sympatico.ca",
  "cgocable.ca",
  "globetrotter.net",
  "telus.net",
]);
export const isMailboxProvider = (domain: string) =>
  MAILBOX_PROVIDERS.has(domain.trim().toLowerCase());

/** The domain without its last dot-part (plomberiesyracuse.com → plomberiesyracuse). */
export const domainLabel = (domain: string) =>
  domain
    .trim()
    .toLowerCase()
    .replace(/\.[^.]*$/, "");

const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
export const isValidEmail = (v: unknown): v is string =>
  typeof v === "string" && EMAIL.test(v.trim());

/** The distance when it is 1 (labels of 5+ characters) or 2 (8+), else null; equal labels never. */
export const typoDistance = (a: string, b: string): number | null => {
  if (a === b || Math.abs(a.length - b.length) > 2) return null;
  const n = Math.min(a.length, b.length);
  if (n < 5) return null;
  const d = osaDistance(a, b);
  return d === 1 || (d === 2 && n >= 8) ? d : null;
};
export const labelTypo = (a: string, b: string): boolean =>
  typoDistance(a, b) !== null;

const domainOf = (email: string) =>
  email.trim().split("@")[1]?.toLowerCase() ?? "";
const localOf = (email: string) => email.trim().split("@")[0];

type Company = {
  id: number;
  name?: string | null;
  email?: unknown;
  website?: unknown;
  is_test?: unknown;
};
type Contact = { id: number; company_id?: unknown; email_jsonb?: unknown };
type EmailMessage = {
  direction?: unknown;
  status?: unknown;
  to_emails?: unknown;
  cc_emails?: unknown;
  sent_at?: unknown;
  created_at?: unknown;
  bounced_at?: unknown;
  first_opened_at?: unknown;
  [key: string]: unknown;
};

type Suppression = {
  channel?: unknown;
  value?: unknown;
  reason?: unknown;
  created_at?: unknown;
  [key: string]: unknown;
};

const time = (v: unknown) => (v ? Date.parse(String(v)) : NaN);

/**
 * The suspect emails of non-test compagnies with a valid email, by name then id. `dismissed` holds
 * "<company id>:<lowercased email>" keys (« Ignorer » is per compagnie and exact email). An address bounced when it is
 * on the bounce suppressions or was the only recipient (to + cc) of a bounced email, and nothing later reached it.
 */
export const findSuspectEmails = (
  companies: Company[],
  contacts: Contact[],
  messages: EmailMessage[],
  dismissed: ReadonlySet<string>,
  suppressions: Suppression[] = [],
): SuspectEmail[] => {
  const contactEmails = new Map<number, string[]>();
  for (const x of [...contacts].sort((p, q) => p.id - q.id)) {
    const list = Array.isArray(x.email_jsonb) ? x.email_jsonb : [];
    for (const e of list) {
      const v = (e as { email?: unknown })?.email;
      if (!isValidEmail(v)) continue;
      const id = Number(x.company_id);
      contactEmails.set(id, [...(contactEmails.get(id) ?? []), v.trim()]);
    }
  }
  const list = (v: unknown) => (Array.isArray(v) ? v : []);
  const addresses = (m: EmailMessage) =>
    [...list(m.to_emails), ...list(m.cc_emails)].map((t) =>
      String(t).trim().toLowerCase(),
    );
  const outbound = messages.filter((m) => m.direction === "outbound");
  const sentAt = (m: EmailMessage) => time(m.sent_at ?? m.created_at);
  const bounceSuppressed = (addr: string) =>
    suppressions.find(
      (q) => q.channel === "email" && q.reason === "bounce" && q.value === addr,
    );
  const bouncedAt = (addr: string): string | null => {
    const events: { at: number; bounced: string }[] = outbound
      .filter((m) => {
        const to = addresses(m);
        return m.status === "bounced" && to.length === 1 && to[0] === addr;
      })
      .map((m) => ({
        at: sentAt(m),
        bounced: String(m.bounced_at ?? m.sent_at ?? m.created_at ?? ""),
      }));
    const sup = bounceSuppressed(addr);
    if (sup)
      events.push({
        at: time(sup.created_at),
        bounced: String(sup.created_at ?? ""),
      });
    let at: string | null = null;
    for (const e of events) {
      const later = outbound.some(
        (n) =>
          addresses(n).includes(addr) &&
          (n.status === "sent" ||
            n.status === "delivered" ||
            (!!n.first_opened_at && n.status !== "bounced")) &&
          sentAt(n) > e.at,
      );
      if (later) continue;
      if (at === null || time(e.bounced) > time(at)) at = e.bounced;
    }
    return at;
  };

  const out: SuspectEmail[] = [];
  for (const c of companies) {
    if (c.is_test === true || !isValidEmail(c.email)) continue;
    const email = c.email.trim();
    const em = email.toLowerCase();
    if (dismissed.has(`${c.id}:${em}`)) continue;
    const dom = domainOf(email);
    const loc = localOf(email);
    const own = contactEmails.get(c.id) ?? [];

    let typo: { domain: string; source: "contact" | "website" } | null = null;
    const host = websiteHost(c.website == null ? null : String(c.website));
    const label = domainLabel(dom);
    // the compagnie's own website or another contact address backs the email up: the other side has the typo
    const backed =
      domainLabel(host ?? "") === label ||
      own.some((e) => domainOf(e) === dom && e.toLowerCase() !== em);
    if (!isMailboxProvider(dom) && !backed) {
      const cands: { domain: string; source: "contact" | "website" }[] =
        own.map((e) => ({ domain: domainOf(e), source: "contact" }));
      if (host && host.includes(".") && !isGenericHost(host))
        cands.push({ domain: host, source: "website" });
      const scored = cands
        .map((k) => ({ ...k, d: typoDistance(label, domainLabel(k.domain)) }))
        .filter((k): k is typeof k & { d: number } => k.d !== null)
        .sort(
          (p, q) =>
            p.d - q.d ||
            Number(p.source === "website") - Number(q.source === "website") ||
            p.domain.localeCompare(q.domain),
        );
      if (scored.length)
        typo = { domain: scored[0].domain, source: scored[0].source };
    }
    const bounced = bouncedAt(em);
    if (!typo && !bounced) continue;

    let suggestion: string | null;
    if (typo) {
      const t = typo;
      const atDomain = own.filter((e) => domainOf(e) === t.domain);
      suggestion =
        atDomain.find((e) => localOf(e).toLowerCase() === loc.toLowerCase()) ??
        atDomain[0] ??
        `${loc}@${t.domain}`;
    } else {
      suggestion = own.find((e) => e.toLowerCase() !== em) ?? null;
    }
    out.push({
      company_id: c.id,
      name: c.name ?? null,
      email,
      reasons: [
        ...(typo ? (["typo_domain"] as const) : []),
        ...(bounced ? (["bounced"] as const) : []),
      ],
      suggestion,
      evidence: {
        ...(typo ? { typo_domain: typo.domain, typo_source: typo.source } : {}),
        ...(bounced ? { bounced_at: bounced } : {}),
        ...(bounced && bounceSuppressed(em)
          ? { suppressed: true as const }
          : {}),
      },
    });
  }
  return out.sort(
    (p, q) =>
      String(p.name ?? "").localeCompare(String(q.name ?? "")) ||
      p.company_id - q.company_id,
  );
};
